import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Alert, api, Spinner, useAsyncAction } from '@pryme/ui';
import { ErrorBox } from '../components/ErrorBox';
import { useDev } from '../lib/context';
import { ROLE_LABEL, type Role } from '../lib/types';

/** /invite#t=<token> — the token stays in the fragment so it never reaches server logs. */
export function InviteAccept() {
  const [token] = useState(() => new URLSearchParams(location.hash.slice(1)).get('t') ?? '');
  const { reloadOrgs, selectOrg } = useDev();
  const accept = useAsyncAction();
  const [done, setDone] = useState<{ organizationId: string; role: Role } | null>(null);
  const navigate = useNavigate();
  const once = useRef(false);

  useEffect(() => {
    // Remove the one-time token from the address bar / history.
    if (location.hash) history.replaceState(null, '', location.pathname + location.search);
  }, []);

  const run = async () => {
    if (once.current) return;
    once.current = true;
    const r = await accept.run(() => api<{ organizationId: string; role: Role }>('/v1/invites/accept', { body: { token } }));
    if (r) {
      setDone(r);
      await reloadOrgs();
      selectOrg(r.organizationId);
    } else once.current = false;
  };

  return (
    <section className="p-card" style={{ maxWidth: 600 }}>
      <span className="p-eyebrow">Organization</span>
      <h1>Team invitation</h1>
      {!token ? (
        <Alert tone="bad">This invite link is incomplete. Ask your teammate for a new link.</Alert>
      ) : done ? (
        <>
          <Alert tone="ok">You joined the organization as <strong>{ROLE_LABEL[done.role]}</strong>.</Alert>
          <button className="p-btn" onClick={() => navigate('/')}>Go to dashboard</button>
        </>
      ) : (
        <>
          <p>You have been invited to join a Pryme developer organization. You join with your current Pryme ID; teammates see only a pseudonymous member reference.</p>
          <button className="p-btn" disabled={accept.busy} onClick={run}>{accept.busy ? <Spinner label="Joining…" /> : 'Accept invitation'}</button>
          <ErrorBox error={accept.error} />
          <p className="p-small" style={{ marginTop: '0.75rem' }}><Link to="/">Not now</Link></p>
        </>
      )}
    </section>
  );
}
